import React, { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { fetchTradeFieldSchema } from '@/context/TradeContext';

/**
 * CR-14 — Trade-Specific Field Schema renderer.
 *
 * Renders ONLY the trade-relevant fields for `trade` (resolved from the
 * backend field-schema endpoint, cached). Purely additive — it never touches
 * the existing appliance/warranty fields, which keep their own UI region.
 *
 * Props:
 *   trade       trade name / group key (e.g. "HVAC Technician")
 *   value       current trade_fields object ({ key: value })
 *   onChange    (nextObj) => void   — fired on every edit (parent holds state)
 *   onCommit    (nextObj) => void   — fired when a field should persist
 *                                     (select: immediately; text: on blur)
 *   readOnly    render label/value pairs only (invoice surface)
 *   heading     section heading text
 *   testIdPrefix  data-testid prefix for fields
 */
export const TradeFieldsSection = ({
  trade,
  value = {},
  onChange,
  onCommit,
  readOnly = false,
  heading = 'Trade Details',
  testIdPrefix = 'trade-field',
}) => {
  const [fields, setFields] = useState([]);

  useEffect(() => {
    let active = true;
    if (!trade) { setFields([]); return; }
    fetchTradeFieldSchema(trade).then((schema) => {
      if (active) setFields(schema?.fields || []);
    });
    return () => { active = false; };
  }, [trade]);

  if (!fields.length) return null;

  const set = (key, v) => onChange?.({ ...(value || {}), [key]: v });

  // ── Read-only (invoice) ──────────────────────────────────────────────
  if (readOnly) {
    const populated = fields.filter((f) => {
      const v = value?.[f.key];
      return v !== undefined && v !== null && v !== '';
    });
    if (!populated.length) return null;
    return (
      <div className="bg-white rounded-xl border p-4" data-testid="invoice-trade-fields">
        {heading ? (
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">{heading}</p>
        ) : null}
        <div className="grid grid-cols-2 gap-2">
          {populated.map((f) => (
            <div key={f.key}>
              <p className="text-[10px] text-gray-400">{f.label}</p>
              <p className="text-xs text-gray-700" data-testid={`${testIdPrefix}-ro-${f.key}`}>
                {String(value[f.key])}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Editable (intake + ticket) ───────────────────────────────────────
  return (
    <div className="space-y-3" data-testid="trade-fields-section">
      {fields.map((f) => (
        <div key={f.key}>
          <Label className="text-xs text-gray-500">{f.label}</Label>
          {f.type === 'textarea' ? (
            <Textarea
              value={value?.[f.key] || ''}
              onChange={(e) => set(f.key, e.target.value)}
              onBlur={() => onCommit?.(value)}
              rows={2}
              className="mt-1"
              data-testid={`${testIdPrefix}-${f.key}`}
            />
          ) : f.type === 'select' ? (
            <Select
              value={value?.[f.key] || ''}
              onValueChange={(v) => {
                const next = { ...(value || {}), [f.key]: v };
                onChange?.(next);
                onCommit?.(next);
              }}
            >
              <SelectTrigger className="mt-1 h-9 text-sm" data-testid={`${testIdPrefix}-${f.key}`}>
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {(f.options || []).map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              type={f.type === 'number' ? 'number' : 'text'}
              value={value?.[f.key] || ''}
              onChange={(e) => set(f.key, e.target.value)}
              onBlur={() => onCommit?.(value)}
              className="mt-1 h-9 text-sm"
              data-testid={`${testIdPrefix}-${f.key}`}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default TradeFieldsSection;
